import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { chip, ib, table } from "../../ui/record";
import { pageHead } from "../empty";

// Settings › Integrations (brief 1B-5, mockup settings/integrations with v20's Calls): the Calls
// tile, and Calls itself: which provider every call goes through (Twilio Voice, Telnyx or
// RingCentral), whether its keys are there, your own phone (it rings first), and Test, which
// calls your phone (through the fake in previews, so nothing real is called). Swapping providers
// changes nothing else. Keys are Netlify settings, never typed here (docs/SETUP.md › Calls).
// Brief 1E-3 (mockup v37/v55 "How each service connects"): the Stripe row: Connect your account
// sends the Admin to Stripe's own pages and back; it shows charges enabled or what Stripe needs.
// Brief 1E-8: one Stripe row per business, each with its own Connect and status.
// Brief 1E-12 (mockup v62): one Card payments row per business a module uses, with the modules.

interface Calls {
  provider: "twilio" | "telnyx" | "ringcentral";
  providers: { id: string; label: string; state: "live" | "test" | "not_live" }[];
  statusUrl: string;
  myPhone: string | null;
  lines: { id: string; name: string }[];
}

const STATE: Record<string, [string, string]> = {
  live: ["Connected", "ok"],
  test: ["Test mode", "warn"],
  not_live: ["Not yet live", "plain"],
};

interface StripeRow {
  ready: boolean;
  status: string;
  level: string;
  connected: boolean;
  chargesEnabled: boolean;
  needs: string[];
  account: string | null;
  /** Whose account it is (brief 1E-8: one per business). */
  business?: { id: string; name: string };
  /** The modules that run under that business (brief 1E-12). */
  usedBy?: string[];
}
interface Stripe extends StripeRow {
  businesses: StripeRow[];
}

/** How each service connects: Stripe's rows, one per business (Connect your account). */
async function connectsCard(): Promise<HTMLElement | null> {
  const query = new URLSearchParams(location.search);
  const back = query.has("stripe");
  // Back from Stripe's pages: the business that was connecting is asked about (brief 1E-8).
  const business = query.get("business");
  const res = back
    ? await api<Stripe>("POST", "/api/settings/stripe/refresh", business ? { business } : {})
    : await api<Stripe>("GET", "/api/settings/stripe");
  if (!res.ok) return null;
  const slot = el("section", { class: "card" });
  const draw = (all: Stripe) => {
    const rows: StripeRow[] = all.businesses.length ? all.businesses : [all];
    const asked = (st: StripeRow) => (st.business ? { business: st.business.id } : {});
    const actions = (st: StripeRow) => {
      const connect = el(
        "button",
        { type: "button", class: "btn sm pri" },
        icon("link-external"),
        st.connected ? " Finish on Stripe" : " Connect",
      );
      connect.disabled = !st.ready;
      if (!st.ready) connect.setAttribute("data-tip", "Card payments aren't set up yet");
      if (st.business) connect.setAttribute("aria-label", `Connect ${st.business.name}`);
      connect.addEventListener("click", async () => {
        connect.disabled = true;
        const r = await api<{ url: string }>("POST", "/api/settings/stripe/connect", asked(st));
        connect.disabled = false;
        if (!r.ok) return toast("Stripe didn't open", r.body.error);
        location.assign(r.body.url);
      });
      const check = ib("sync", "Check with Stripe again", async () => {
        const r = await api<Stripe>("POST", "/api/settings/stripe/refresh", asked(st));
        if (!r.ok) return toast("Stripe didn't answer", r.body.error);
        draw(r.body);
      });
      return el(
        "span",
        { class: "rowacts" },
        ...(st.chargesEnabled ? [] : [connect]),
        ...(st.connected ? [check] : []),
      );
    };
    slot.replaceChildren(
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("plug"), " How each service connects"),
          el(
            "span",
            { class: "small muted" },
            "Each service is connected by signing in on its own page and approving FlowCare.",
          ),
        ),
      ),
      table(
        [
          {
            h: "For",
            f: (st) =>
              el(
                "span",
                {},
                el("b", {}, st.business ? `Card payments · ${st.business.name}` : "Card payments"),
                el("div", { class: "small muted" }, "Stripe"),
              ),
          },
          { h: "How", f: () => chip("Company account", "info") },
          { h: "Status", f: (st) => chip(st.status, st.level) },
          {
            h: "What happens",
            f: (st) =>
              el(
                "span",
                {},
                st.business
                  ? `Payments are deposited in ${st.business.name}'s own Stripe account; FlowCare's fee is added per payment.`
                  : "Open or link the company's own Stripe account on Stripe's pages. Payments are deposited there; FlowCare's fee is added per payment.",
                st.usedBy?.length
                  ? el("div", { class: "small muted" }, `Used by ${st.usedBy.join(", ")}`)
                  : null,
                st.needs.length
                  ? el(
                      "div",
                      { class: "small muted" },
                      `Stripe still needs: ${st.needs.join(", ")}`,
                    )
                  : st.account
                    ? el("div", { class: "small muted" }, `Account ${st.account}`)
                    : null,
              ),
          },
          { h: "", f: actions },
        ],
        rows,
        undefined,
        "How each service connects",
      ),
    );
  };
  draw(res.body);
  if (back) history.replaceState(null, "", location.pathname);
  return slot;
}

const ABOUT = "Call from any record or the inbox through the provider connected here";

export async function integrationsScreen(host: HTMLElement, ctx: ScreenContext) {
  const res = await api<Calls>("GET", "/api/settings/calls");
  if (ctx.record && ctx.record !== "calls") {
    host.append(
      pageHead(ctx),
      el("p", { class: "note", role: "alert" }, "That integration isn't here."),
    );
    return;
  }
  if (!res.ok) {
    host.append(
      pageHead(ctx),
      el("p", { class: "note", role: "alert" }, "Only an Admin can see and change integrations."),
    );
    return;
  }
  let calls = res.body;
  const stateOf = () => calls.providers.find((p) => p.id === calls.provider)?.state ?? "not_live";

  if (!ctx.record) {
    const [word, level] = STATE[stateOf()] ?? ["Not yet live", "plain"];
    const tile = el(
      "button",
      { type: "button", class: "card tile", style: "padding:16px", "aria-label": "Calls" },
      el(
        "span",
        { style: "display:flex;justify-content:space-between;align-items:center;gap:8px" },
        el(
          "span",
          { style: "display:flex;gap:8px;align-items:center;font-weight:700" },
          icon("device-mobile"),
          " Calls",
        ),
        chip(word, level),
      ),
      el("span", { class: "muted" }, ABOUT),
      el(
        "span",
        { class: "small" },
        `${calls.providers.find((p) => p.id === calls.provider)?.label ?? ""} · company account`,
      ),
    );
    tile.addEventListener("click", () => ctx.go("/settings/integrations/calls"));
    const lines = el(
      "button",
      { type: "button", class: "chipbtn" },
      icon("plug"),
      " Lines and mailboxes: Connected inboxes",
    );
    lines.addEventListener("click", () => ctx.go("/inbox/sources"));
    host.append(
      pageHead(ctx, "Connect a service once, choose which modules use it, and test it."),
      el("div", { class: "grid3" }, tile),
      el("div", { class: "linkrow" }, lines),
    );
    const connects = await connectsCard();
    if (connects) host.append(connects);
    return;
  }

  ctx.crumb("Calls");
  const page = el("div", {});
  host.append(page);
  const log: { ok: boolean; text: string; at: string }[] = [];

  function draw() {
    const [word, level] = STATE[stateOf()] ?? ["Not yet live", "plain"];
    const prov = el(
      "select",
      { id: "in-prov" },
      ...calls.providers.map((p) =>
        el(
          "option",
          { value: p.id, selected: p.id === calls.provider },
          `${p.label}${p.state === "not_live" ? " (not yet live)" : ""}`,
        ),
      ),
    );
    const phone = el("input", {
      id: "in-me",
      type: "tel",
      value: calls.myPhone ?? "",
      placeholder: "410-555-0123",
      autocomplete: "tel",
    });
    const save = el("button", { class: "btn pri", type: "submit" }, icon("check"), " Save");
    const test = el(
      "button",
      { class: "btn", type: "button" },
      icon("device-mobile"),
      " Test: call my phone",
    );
    const form = el(
      "form",
      { class: "form", style: "max-width:none" },
      el(
        "div",
        { class: "field" },
        el("label", { for: "in-prov" }, "Provider"),
        prov,
        el(
          "span",
          { class: "hint" },
          "Every call (the inbox and any record's phone number) goes through the provider picked here.",
        ),
      ),
      el(
        "div",
        { class: "field" },
        el("label", {}, "Keys"),
        el(
          "span",
          { class: "small" },
          "The company's own account. A provider without keys is not yet live.",
        ),
      ),
      el(
        "div",
        { class: "field" },
        el("label", { for: "in-me" }, "Phone to ring first (then the call connects)"),
        phone,
      ),
      el(
        "div",
        { class: "field" },
        el("label", {}, "Status address"),
        el("code", { class: "small" }, calls.statusUrl),
        el("span", { class: "hint" }, "Where the provider says what happened to each call."),
      ),
      el("div", { class: "actions" }, save, test),
    );
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      save.disabled = true;
      const put = await api<Calls>("PUT", "/api/settings/calls", {
        provider: prov.value,
        myPhone: phone.value.trim(),
      });
      save.disabled = false;
      if (!put.ok) return toast("That wasn't saved", put.body.error);
      calls = put.body;
      draw();
      toast(
        "Saved",
        `Calls go through ${calls.providers.find((p) => p.id === calls.provider)?.label}`,
      );
    });
    test.addEventListener("click", async () => {
      test.disabled = true;
      const put = await api<Calls>("PUT", "/api/settings/calls", { myPhone: phone.value.trim() });
      if (put.ok) calls = put.body;
      const t = await api<{ steps: { ok: boolean; text: string }[] }>(
        "POST",
        "/api/settings/calls/test",
        { provider: prov.value },
      );
      test.disabled = false;
      const at = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
      for (const s of t.ok
        ? t.body.steps
        : [{ ok: false, text: t.body.error ?? "The test didn't run." }])
        log.unshift({ ...s, at });
      draw();
    });

    const results = el(
      "section",
      { class: "card", "aria-label": "Test results" },
      el("div", { class: "cardhead" }, el("h2", {}, "Test results")),
      log.length
        ? el(
            "div",
            { class: "thread" },
            ...log.map((l) =>
              el(
                "div",
                { class: `msg${l.ok ? "" : " out"}` },
                el("span", { class: "meta" }, `${l.at} · ${l.ok ? "OK" : "Check"}`),
                el("span", { class: "body" }, l.text),
              ),
            ),
          )
        : el("p", { class: "muted" }, "Run a test to see what happens, step by step."),
    );
    page.replaceChildren(
      el(
        "div",
        { class: "head" },
        el(
          "div",
          { class: "t" },
          el("div", { class: "eyebrow" }, "Integration"),
          el("h1", {}, icon("plug", "h1i"), "Calls"),
          el("div", { class: "muted" }, ABOUT),
        ),
        el("div", { class: "actions" }, chip(word, level)),
      ),
      el(
        "div",
        { class: "grid2" },
        el("section", { class: "card" }, el("h2", {}, "Connection"), form),
        results,
      ),
    );
  }
  draw();
}
